import { useMemo, useEffect, useState } from "react";
import { authApi } from "../../features/auth/api"
import { UserRole } from "@repo/types";


/**
 * Custom hook to track the current authentication status of the user.
 * * It initializes the status based on a synchronous check from `authApi.isAuthenticated()`.
 * It listens for the browser's 'storage' event, which fires when data in local storage
 * is changed by another window/tab, and updates the authentication state accordingly.
 *
 * @returns {boolean} The current authentication status (true if authenticated, false otherwise).
 */
const useAuthStatusChange = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(authApi.isAuthenticated());

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authApi.isAuthenticated());
    };
    window.addEventListener('storage', checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  return isAuthenticated;
};


/**
 * Custom hook to retrieve the current user's primary role, ensuring it updates dynamically.
 *
 * This hook relies on the `useAuthStatusChange` hook to react to authentication state
 * changes (e.g., login/logout across tabs via the 'storage' event). The user role
 * is memoized and recalculated only when the authentication status changes.
 *
 * @returns {UserRole} The primary role of the authenticated user. Returns an empty UserRole ("")
 * if the user is not currently authenticated.
 */
export const useGetRoleCurrentUser = () => {
  const isAuthenticated = useAuthStatusChange();

  const role = useMemo(() => {
    if (!isAuthenticated) {
      return "";
    }

    return authApi.getCurrentUserRole() as UserRole;
  }, [isAuthenticated]);

  return role;
};
